import React, { useState, useRef, useEffect } from 'react';

const getDaysInMonth = (year, month) => {
  return new Date(year, month + 1, 0).getDate();
};

const getFirstDayOfWeek = (year, month) => {
  return new Date(year, month, 1).getDay();
};

const pad = (n) => String(n).padStart(2, '0');

const formatDate = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

const DateSelect = ({ value, onChange, placeholder = 'Select date', min, max, bookedDates = [] }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const today = new Date();
  const initial = value ? new Date(value) : today;
  const [year, setYear] = useState(initial.getFullYear());
  const [month, setMonth] = useState(initial.getMonth());

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);

  const handleSelect = (d) => {
    const dateStr = formatDate(year, month, d);
    onChange && onChange(dateStr);
    setOpen(false);
  };

  const isSelected = (d) => {
    if (!value) return false;
    const sel = new Date(value);
    return sel.getFullYear() === year && sel.getMonth() === month && sel.getDate() === d;
  };

  const canSelect = (d) => {
    const date = new Date(year, month, d);
    if (min && date < new Date(min)) return false;
    if (max && date > new Date(max)) return false;
    return true;
  };

  const isDateBooked = (d) => {
    const date = new Date(year, month, d);
    return bookedDates.some(bookedDate => {
      const booked = new Date(bookedDate);
      return booked.getFullYear() === date.getFullYear() &&
             booked.getMonth() === date.getMonth() &&
             booked.getDate() === date.getDate();
    });
  };

  const displayValue = value ? new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-left bg-white hover:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent flex items-center justify-between transition-colors"
      >
        <span className={displayValue ? 'text-gray-900 font-medium' : 'text-gray-400'}>
          {displayValue || placeholder}
        </span>
        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2v-7a2 2 0 00-2-2H5a2 2 0 00-2 2v7a2 2 0 002 2z" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 p-4 w-80">
          <div className="flex items-center justify-between mb-2">
            <button onClick={() => setMonth(m => m === 0 ? 11 : m - 1) || setYear(y => month === 0 ? y - 1 : y)} className="p-1 rounded hover:bg-gray-100">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <span className="font-medium text-gray-700">{new Date(year, month).toLocaleString(undefined, { month: 'long', year: 'numeric' })}</span>
            <button onClick={() => setMonth(m => m === 11 ? 0 : m + 1) || setYear(y => month === 11 ? y + 1 : y)} className="p-1 rounded hover:bg-gray-100">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-xs text-center text-gray-400 mb-1">
            {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array(firstDay).fill(null).map((_, i) => <div key={i} />)}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(d => (
              <button
                key={d}
                type="button"
                disabled={!canSelect(d) || isDateBooked(d)}
                onClick={() => canSelect(d) && !isDateBooked(d) && handleSelect(d)}
                className={`w-9 h-9 rounded-lg text-sm font-medium transition-all
                  ${isSelected(d) ? 'bg-amber-500 text-white shadow-md' :
                    isDateBooked(d) ? 'bg-gray-200 text-gray-400 cursor-not-allowed' :
                    canSelect(d) ? 'hover:bg-amber-50 hover:text-amber-700 text-gray-700' : 'text-gray-300 cursor-not-allowed'}`}
                title={isDateBooked(d) ? 'This date is already booked' : ''}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateSelect;
